import React from 'react'

import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { AddBox, ChevronRight } from '@mxenabled/mx-icons'

import { InstitutionLogo, Text } from '@mxenabled/mxui'

import {
  ConnectCard,
  Drawer,
  useAccountStore,
  useAppConfig,
  useGlobalCopyStore,
  useGoalStore,
} from '../../../common'
import { type Account, Goal } from '../../../core'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface ChangeAccountProps {
  goal: Goal
}

export const ChangeAccountAction = ({ goal }: ChangeAccountProps) => {
  const { visibleCheckingAccounts: checkingAccounts, visibleSavingsAccounts: savingsAccounts } =
    useAccountStore()
  const { accounts: accountsCopy, goals: copy } = useGlobalCopyStore()
  const { config } = useAppConfig()
  const { setAlert, setSelectedGoal, updateGoal } = useGoalStore()

  const isNew = !goal.guid

  const [isOpen, setIsOpen] = React.useState(false)

  const updateAccount = async ({ balance, guid }: Account) => {
    const updatedGoal = { ...goal, account_guid: guid, current_amount: balance || 0 }
    if (isNew) {
      setSelectedGoal(updatedGoal)
    } else {
      await updateGoal(updatedGoal)
      setAlert(copy.alert_account_changed)
    }
    setIsOpen(false)
  }

  return (
    <Stack>
      {isNew && !goal.account_guid ? (
        <ListItem disableGutters={true} disablePadding={true} sx={{ bgcolor: 'background.paper' }}>
          <ListItemButton onClick={() => setIsOpen(true)}>
            <ListItemText>
              <Stack alignItems="center" color="primary.main" direction="row" gap={8}>
                <AddBox filled={true} />
                {copy.details_add_account}
              </Stack>
            </ListItemText>
            <ChevronRight color="primary" />
          </ListItemButton>
        </ListItem>
      ) : (
        <Stack sx={{ gap: 8, my: 24, mx: 24 }}>
          <Button onClick={() => setIsOpen(true)} variant="text">
            {copy.details_change_account}
          </Button>
          <Text color="text.secondary" sx={{ textAlign: 'center' }} variant="subtitle1">
            {buildDynamicCopy(copy.details_change_account_description, goal.name) as string}
          </Text>
        </Stack>
      )}
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} title={copy.details_change_account}>
        <Stack>
          {checkingAccounts.length > 0 && (
            <Text bold={true} sx={{ mb: 4, ml: 24, mt: 12 }} variant="caption">
              {accountsCopy.account_types['1']}
            </Text>
          )}
          <List sx={{ bgcolor: 'background.paper' }}>
            {checkingAccounts.map((account) => (
              <ListItem key={account.guid}>
                <ListItemButton onClick={() => updateAccount(account)}>
                  <ListItemAvatar>
                    <InstitutionLogo
                      alt={account?.name || ''}
                      institutionGuid={account?.institution_guid || ''}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={account?.name || account?.feed_name}
                    secondary={formatCurrency(account?.balance || 0, '0,0.00')}
                  />
                  <ChevronRight />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {savingsAccounts.length > 0 && (
            <Text bold={true} sx={{ mb: 4, ml: 24, mt: 12 }} variant="caption">
              {accountsCopy.account_types['2']}
            </Text>
          )}
          <List sx={{ bgcolor: 'background.paper' }}>
            {savingsAccounts.map((account) => (
              <ListItem key={account.guid}>
                <ListItemButton onClick={() => updateAccount(account)}>
                  <ListItemAvatar>
                    <InstitutionLogo
                      alt={account?.name || ''}
                      institutionGuid={account?.institution_guid || ''}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={account?.name || account?.feed_name}
                    secondary={formatCurrency(account?.balance || 0, '0,0.00')}
                  />
                  <ChevronRight />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {config.show_connections_widget_in_master && (
            <ConnectCard
              description={copy.connect_description}
              label={copy.connect_button}
              title={copy.connect_title}
            />
          )}
        </Stack>
      </Drawer>
    </Stack>
  )
}
